import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Layout } from '../components/common/Layout';
import { SEOHead } from '../components/seo/SEOHead';
import { StructuredData } from '../components/seo/StructuredData';
import { blogService } from '../services/firebase/blog';
import { BlogPost } from '../types/blog';

const formatDate = (date?: Date | null) => {
  if (!date) {
    return '';
  }

  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
  }).format(date);
};

const stripHtml = (value: string) => value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

export const BlogPostPage: React.FC = () => {
  const { slug = '' } = useParams();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        setError(null);
        const loaded = await blogService.getPublicPostBySlug(slug);

        if (!loaded) {
          setError('This article is not available.');
          return;
        }

        setPost(loaded);
      } catch (err) {
        console.error('Failed to load blog post:', err);
        setError('Unable to load this article right now.');
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  useEffect(() => {
    if (loading) return;
    if (typeof window !== 'undefined') {
      (window as any).__PRERENDER_READY__ = true;
    }
  }, [loading]);

  const canonical = post?.canonicalUrl || `https://trainedup.ai/blog/${slug}`;
  const description = post?.seoDescription || post?.excerpt || 'Read insights from trAInedup.ai.';
  const title = post?.seoTitle || post?.title || 'Blog Article';
  const ogImage = post?.ogImageUrl || post?.coverImageUrl;

  const articleSchema = useMemo(() => {
    if (!post) {
      return null;
    }

    const fallbackDate = post.publishAt || post.publishedAt || post.createdAt;

    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description,
      datePublished: fallbackDate ? fallbackDate.toISOString() : undefined,
      dateModified: post.updatedAt ? post.updatedAt.toISOString() : undefined,
      image: ogImage ? [ogImage] : undefined,
      author: {
        '@type': 'Person',
        name: post.authorName || 'trAInedup.ai Team',
      },
      publisher: {
        '@type': 'Organization',
        name: 'trAInedup.ai',
        logo: {
          '@type': 'ImageObject',
          url: 'https://trainedup.ai/tulogo.png',
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonical,
      },
    };
  }, [canonical, description, ogImage, post]);

  const readingMinutes = useMemo(() => {
    if (!post) {
      return 0;
    }

    const words = stripHtml(post.content).split(' ').filter(Boolean).length;
    return Math.max(1, Math.ceil(words / 220));
  }, [post]);

  const handleShare = (network: 'linkedin' | 'x' | 'facebook' | 'email' | 'copy') => {
    if (!post) {
      return;
    }

    const currentUrl = window.location.href;
    const shareTitle = encodeURIComponent(post.title);
    const shareUrl = encodeURIComponent(currentUrl);

    if (network === 'copy') {
      navigator.clipboard.writeText(currentUrl);
      return;
    }

    if (network === 'linkedin') {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`, '_blank', 'noopener,noreferrer');
      return;
    }

    if (network === 'x') {
      window.open(`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`, '_blank', 'noopener,noreferrer');
      return;
    }

    if (network === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`, '_blank', 'noopener,noreferrer');
      return;
    }

    window.location.href = `mailto:?subject=${shareTitle}&body=${shareUrl}`;
  };

  return (
    <Layout>
      <SEOHead
        title={title}
        description={description}
        canonical={canonical}
        ogType="article"
        ogImage={ogImage}
      />
      {articleSchema && <StructuredData data={articleSchema} />}

      <section className="max-w-4xl mx-auto px-4 py-10">
        {loading && <p className="text-neutral-600">Loading article...</p>}

        {!loading && error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div>
        )}

        {!loading && !error && post && (
          <article>
            <Link to="/blog" className="text-primary hover:underline">
              Back to blog
            </Link>

            <h1 className="mt-4 font-montserrat text-4xl font-bold text-neutral-900">{post.title}</h1>
            <div className="mt-3 text-sm text-neutral-600">
              <span>{formatDate(post.publishAt || post.publishedAt || post.createdAt)}</span>
              <span className="mx-2">•</span>
              <span>{readingMinutes} min read</span>
              <span className="mx-2">•</span>
              <span>By {post.authorName || 'trAInedup.ai Team'}</span>
            </div>

            {post.coverImageUrl && (
              <img
                src={post.coverImageUrl}
                alt={post.title}
                className="mt-6 w-full max-h-[460px] rounded-2xl object-cover"
              />
            )}

            <div className="mt-8 prose prose-neutral max-w-none" dangerouslySetInnerHTML={{ __html: post.content }} />

            <div className="mt-10 border-t border-neutral-200 pt-6">
              <p className="text-sm font-semibold text-neutral-900">Share this article</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" className="rounded-md border px-3 py-2 text-sm" onClick={() => handleShare('linkedin')}>
                  LinkedIn
                </button>
                <button type="button" className="rounded-md border px-3 py-2 text-sm" onClick={() => handleShare('x')}>
                  X
                </button>
                <button type="button" className="rounded-md border px-3 py-2 text-sm" onClick={() => handleShare('facebook')}>
                  Facebook
                </button>
                <button type="button" className="rounded-md border px-3 py-2 text-sm" onClick={() => handleShare('email')}>
                  Email
                </button>
                <button type="button" className="rounded-md border px-3 py-2 text-sm" onClick={() => handleShare('copy')}>
                  Copy Link
                </button>
              </div>
            </div>
          </article>
        )}
      </section>
    </Layout>
  );
};
